import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

import { Phone, Star } from 'lucide-react';


const Reviews = () => {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    review: ''
  });

  // Initial reviews with submitted reviews state
  const initialReviews = [
    {
      id: 1,
      name: 'Rajesh Kumar',
      rating: 5,
      date: '2024-01-15',
      review: 'Excellent service! The driver was punctual and professional. The car was clean and comfortable. Highly recommend Advait Tour and Travels for anyone looking for reliable taxi service in Ludhiana.',
      image: 'https://ui-avatars.com/api/?name=Rajesh+Kumar&background=3b82f6&color=fff'
    },
    {
      id: 2,
      name: 'Priya Sharma',
      rating: 5,
      date: '2024-01-12',
      review: 'Booked a cab from Ludhiana to Delhi. The journey was smooth and the driver was very courteous. Great experience with Advait Tour and Travels!',
      image: 'https://ui-avatars.com/api/?name=Priya+Sharma&background=ec4899&color=fff'
    },
    {
      id: 3,
      name: 'Amit Singh',
      rating: 4,
      date: '2024-01-10',
      review: 'Good service overall. The booking process was easy and the cab arrived on time. The only suggestion would be to add more payment options.',
      image: 'https://ui-avatars.com/api/?name=Amit+Singh&background=10b981&color=fff'
    },
    {
      id: 4,
      name: 'Neha Gupta',
      rating: 5,
      date: '2024-01-08',
      review: 'Amazing experience! Used their service for a family trip to Chandigarh. The Innova Crysta was spacious and comfortable. Driver was very helpful. Will definitely use again!',
      image: 'https://ui-avatars.com/api/?name=Neha+Gupta&background=f59e0b&color=fff'
    },
    {
      id: 5,
      name: 'Vikram Mehta',
      rating: 5,
      date: '2024-01-05',
      review: 'Professional service at reasonable rates. Booked for corporate travel and they provided excellent service. Highly recommended for business travelers.',
      image: 'https://ui-avatars.com/api/?name=Vikram+Mehta&background=8b5cf6&color=fff'
    },
    {
      id: 6,
      name: 'Simran Kaur',
      rating: 4,
      date: '2024-01-03',
      review: 'Very satisfied with the service. The cab was clean and well-maintained. Driver was polite and drove safely. Good value for money.',
      image: 'https://ui-avatars.com/api/?name=Simran+Kaur&background=ef4444&color=fff'
    }
  ];

  const [customerReviews, setCustomerReviews] = useState([]);

  // Load reviews from localStorage on mount
  useEffect(() => {
    const savedReviews = localStorage.getItem('advaitTravelsReviews');
    if (savedReviews) {
      try {
        const parsedReviews = JSON.parse(savedReviews);
        setCustomerReviews(parsedReviews);
      } catch (error) {
        console.error('Error loading reviews:', error);
        setCustomerReviews(initialReviews);
      }
    } else {
      setCustomerReviews(initialReviews);
    }
  }, []);

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e?.key === 'advaitTravelsReviews' && e?.newValue) {
        try {
          const updatedReviews = JSON.parse(e?.newValue);
          setCustomerReviews(updatedReviews);
        } catch (error) {
          console.error('Error parsing storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!formData?.name || !formData?.email || !formData?.review || rating === 0) {
      alert('Please fill in all fields and provide a rating');
      return;
    }
    
    // Create new review object
    const newReview = {
      id: Date.now(),
      name: formData?.name,
      rating: rating,
      date: new Date()?.toISOString()?.split('T')?.[0],
      review: formData?.review,
      image: `https://ui-avatars.com/api/?name=${encodeURIComponent(formData?.name)}&background=3b82f6&color=fff`
    };
    
    // Add new review to the beginning of the list
    const updatedReviews = [newReview, ...customerReviews];
    setCustomerReviews(updatedReviews);
    
    // Save to localStorage
    localStorage.setItem('advaitTravelsReviews', JSON.stringify(updatedReviews));
    
    alert('Thank you for your review! Your feedback has been published.');
    setFormData({ name: '', email: '', review: '' });
    setRating(0);
    
    // Scroll to reviews section
    setTimeout(() => {
      const reviewsSection = document.getElementById('reviews-section');
      if (reviewsSection) {
        reviewsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const renderStars = (count) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${index < count ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <>
      <SEO
        title="Customer Reviews - Advait Tour and Travels | Real Experiences"
        description="Read genuine customer reviews and testimonials about Advait Tour and Travels taxi services. See what our satisfied customers have to say about our Ludhiana to Delhi, Chandigarh to Haridwar, and other routes."
        keywords="advait tour and travels reviews, customer testimonials, taxi service reviews, ludhiana cab reviews, customer feedback, advait tour and travels ratings"
        canonicalUrl={`${window.location?.origin}/reviews`}
        ogTitle="Customer Reviews - Advait Tour and Travels"
        ogDescription="Read genuine customer reviews and testimonials about our taxi services."
        ogUrl={`${window.location?.origin}/reviews`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Advait Tour and Travels",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": customerReviews?.length?.toString()
          }
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <Star className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Customer Reviews
              </h1>
              <p className="text-xl text-white max-w-3xl mx-auto">
                Real Experiences, Real Satisfaction
              </p>
              <div className="flex items-center justify-center gap-2 mt-6">
                <div className="flex">
                  {renderStars(5)}
                </div>
                <span className="text-2xl font-bold text-white">4.8/5</span>
                <span className="text-white">({customerReviews?.length} reviews)</span>
              </div>
            </div>
          </div>

          {/* Review Form Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Share Your Experience
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData?.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Rating *
                  </label>
                  <div className="flex gap-2">
                    {Array.from({ length: 5 }, (_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setRating(index + 1)}
                        onMouseEnter={() => setHoverRating(index + 1)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`w-8 h-8 transition-colors ${
                            index < (hoverRating || rating)
                              ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Review *
                  </label>
                  <textarea
                    name="review"
                    value={formData?.review}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Share your experience with us..."
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors duration-300"
                >
                  Submit Review
                </button>
              </form>
            </div>
          </div>

          {/* Customer Reviews Display */}
          <div id="reviews-section" className="container mx-auto px-4 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What Our Customers Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {customerReviews?.map((review) => (
                <div
                  key={review?.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={review?.image}
                      alt={review?.name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{review?.name}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(review?.date)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {renderStars(review?.rating)}
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {review?.review}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Reviews;